/**
 * Canonical list of tables and columns excluded from Zero replication.
 *
 * Consumed by:
 *  - setupDatabaseConstructs.ts (builds the Postgres publication)
 *  - drizzle-zero.config.ts (derives the client schema at module load)
 *  - isNonReplicatedField (UI-side guard for FieldTarget)
 *
 * excludedTables / secretColumns / pendingDropColumns drop from both the
 * client schema and publication. clientSchemaIgnoredTables/Columns drop only
 * from the client schema, for the first deploy of a Zero replication
 * contraction.
 */

import type { FieldTarget } from "../shared/connectors/types";

export const excludedTables = new Set([
  "tenant_limits",
  "storage_limit_blocks",
  "session",
  "account",
  "verification",
  "execution_claims",
  "build_images",
  "execution_history",
  "execution_history_files",
  "execution_failures",
  "workflow_run_status_updates",
  "step_counter_expired_slots",
  "step_counter_slots",
  "workflow_run_daily_counter_slots",
  "volume_write_locks",
  "device_authorizations",
  "oauth_auth_code",
  // Server-side only: monitoring reads the ai_usage_daily rollup, so raw
  // usage records never reach clients or the replica.
  "ai_usage_records",
  // Git smart-HTTP server state: refs and raw git objects, served only over
  // the git protocol.
  "space_git_refs",
  "space_git_objects",
  "space_git_projections",
  // Server-side connector metering; monitoring and spend enforcement query it directly.
  "connector_usage_daily",
  // Single-row stack policy read by the resource-limit triggers; no client use.
  "stack_limits",
  // Server-only Tines units ledger; monitoring and admission read it directly.
  "tines_units_usage",
  "space_storage_usage",
  "space_storage_breakdown",
  // Server-only storage meter samples.
  "storage_usage",
  // Write-only signup audit row. Snowflake reads it from the RDS export;
  // nothing in the product queries it, so it stays off the Zero replica.
  "signup_form_completions",
  "step_tree_cleanup",
  // Tree headers only anchor server-side locking and collection; clients read
  // trees through commit_steps and step_tree_files.
  "step_trees",
  // Server-only queue of workflows whose unreachable commits await collection.
  "workflow_commit_cleanup",
]);

export const secretColumns: Record<string, Set<string>> = {
  custom_domain_setups: new Set(["certificate_request_id", "next_check_at"]),
  tenants: new Set(["license"]),
  chats: new Set(["last_loop_caller"]),
  workflow_runs: new Set(["external_groups", "used_caller_grant"]),
  tenant_keys: new Set(["wrapped_private_key", "wrapped_symmetric_key"]),
  sso_provider: new Set([
    "oidc_config",
    "saml_config",
    "scim_token_hash",
    "scim_request_count",
    "scim_last_request",
  ]),
  oauth_connections: new Set([
    "client_secret",
    "access_token",
    "access_token_refresh_at",
    "refresh_token",
  ]),
  connected_apps: new Set(["secrets", "oauth_client_secret"]),
  ai_providers: new Set(["api_key"]),
  apikey: new Set([
    "key",
    "refill_interval",
    "refill_amount",
    "last_refill_at",
    "rate_limit_enabled",
    "rate_limit_time_window",
    "rate_limit_max",
    "request_count",
    "remaining",
    "permissions",
    "metadata",
  ]),
  // external_email and external_groups are a partner-IdP visitor's PII with no
  // client reader.
  oauth_grant: new Set([
    "refresh_token_hash",
    "external_email",
    "external_groups",
  ]),
};

// All application reads and writes must stop before this marker ships; physical drops require it in a stable release of an earlier major.
export const pendingDropColumns: Record<string, Set<string>> = {
  oauth_connections: new Set([
    "client_id",
    "client_secret",
    "has_client_secret",
  ]),
  // Superseded by network_usage_buckets; soaked in clientSchemaIgnoredColumns
  // since 2026-07-22.
  execution_stats: new Set(["egress_bytes", "ingress_bytes"]),
  stack_limits: new Set(["volumes_per_space"]),
};

// Excluded tables can still have server readers; this additional marker records their retirement before a later major drops them.
export const pendingDropTables = new Set<string>([
  // Superseded by storage_usage.
  "space_storage_usage",
  "space_storage_breakdown",
  "tenant_limits", // Dead: nothing reads or writes it.
]);

// First deploy: stop new clients declaring these tables/columns, while keeping
// them in the publication for stale clients. Next deploy: move them into
// excludedTables, secretColumns, or pendingDropColumns.
export const clientSchemaIgnoredTables = new Set<string>([
  "build_caches",
  "commit_step_layers",
]);

export const clientSchemaIgnoredColumns: Record<string, Set<string>> = {};

export function isPublicationExcludedColumn(
  table: string,
  column: string
): boolean {
  return (
    (secretColumns[table]?.has(column) ?? false) ||
    (pendingDropColumns[table]?.has(column) ?? false)
  );
}

export function isNonReplicatedField(target: FieldTarget): boolean {
  return (
    target.kind === "secrets" ||
    (target.kind === "oauthConnection" && target.field === "clientSecret")
  );
}
